import { z } from 'zod';
import {
  CurrencyEnum,
  DeltaDirectionSchema,
  MoneySchema,
  PaginationParamsSchema,
  PaginationResponseSchema,
} from '../common';
import { ChatReplayManifestSchema } from '../chat/chat.schemas';
import { CtaMode } from '../payment';
import {
  DiscoverEvents,
  EventAccessModel,
  EventCategory,
  EventProductionMode,
  EventReplayAccess,
  EventState,
  EventStreamingMode,
  EventVisibility,
  MyEventsTabSchema,
  RegistrationStatus,
  ViewerRegistrationView,
} from './event.enums';

export const CreateEventRequestSchema = z.object({
  title: z.string().max(120),
  description: z.string().max(2000),
  category: z.enum(EventCategory),
  scheduledStartAt: z.iso.datetime(),
  scheduledEndAt: z.iso.datetime(),
  venueLocation: z.string(),
  visibility: z.enum(EventVisibility),
  accessModel: z.enum(EventAccessModel),
  ticketPrice: z.number().min(0).optional(),
  maxTickets: z.number().min(1).optional(),
  timezone: z.string().default('Africa/Lagos'),
  replayAccess: z.enum(EventReplayAccess),
  retentionDays: z.number().default(30),
  tags: z.array(z.string()).optional(),
  geoRestrictions: z.record(z.string(), z.any()).optional(),
});

export const UpdateEventRequestSchema = CreateEventRequestSchema.partial();

export const EventCardCtaSchema = z.object({
  kind: z.enum(CtaMode),
  price: MoneySchema.optional(),
});

export const EventResponseSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string(),
  category: z.enum(EventCategory),
  coverImageUrl: z.url().optional(),
  venueLocation: z.string(),
  scheduledStartAt: z.iso.datetime(),
  scheduledEndAt: z.iso.datetime(),
  timezone: z.string(),
  visibility: z.enum(EventVisibility),
  accessModel: z.enum(EventAccessModel),
  ticketPrice: MoneySchema.nullable(),
  maxTickets: z.number().optional(),
  eventState: z.enum(EventState),
  shareableUrl: z.url(),
  hostId: z.uuid(),
  isFeatured: z.boolean(),
  replayAccess: z.enum(EventReplayAccess),
  replayAvailableUntil: z.iso.datetime().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  publishedAt: z.iso.datetime().optional(),
  liveStartedAt: z.iso.datetime().optional(),
  liveEndedAt: z.iso.datetime().optional(),
});

export const EventCardSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  timezone: z.string(),
  coverImageUrl: z.url().nullable(),
  previewClipUrl: z.string().nullable(),
  category: z.enum(EventCategory),
  scheduledStartLocal: z.string(),
  scheduledEndLocal: z.string(),
  scheduledStart: z.iso.datetime(),
  scheduledEnd: z.iso.datetime(),
  state: z.enum(EventState),
  accessModel: z.enum(EventAccessModel),
  ticketPrice: MoneySchema.nullable(),
  isFeatured: z.boolean(),
  hostDisplayName: z.string().nullable(),
  viewerCount: z.number().int().min(0),
  cta: EventCardCtaSchema.optional(),
  durationMinutes: z.number().int().nonnegative().nullable().optional(),
  viewer: z.object({
    registration: z.enum(ViewerRegistrationView),
    progressPct: z.number().min(0).max(100).optional(),
    canResume: z.boolean().optional(),
  }),
});

export const ViewerHomeResponseSchema = z.object({
  continueWatching: z.array(EventCardSchema),
  liveNow: z.array(EventCardSchema),
  upcoming: z.array(EventCardSchema),
  recommended: z.array(EventCardSchema),
  featured: EventCardSchema.nullable(),
});

export const SearchEventsQuerySchema = PaginationParamsSchema.extend({
  category: z.enum(EventCategory).optional(),
  accessModel: z.enum(EventAccessModel).optional(),
  section: z.enum(DiscoverEvents).optional(),
  q: z
    .string()
    .trim()
    .max(80)
    .optional()
    .transform((value) => (value && value.length > 0 ? value : undefined)),
});

export const DiscoverAggregateResponseSchema = z.object({
  mode: z.literal('AGGREGATE'),
  featured: EventCardSchema.nullable(),
  liveNow: z.array(EventCardSchema),
  upcoming: z.array(EventCardSchema),
  recommended: z.array(EventCardSchema),
  past: z.array(EventCardSchema),
});

export const DiscoverSectionResponseSchema = PaginationResponseSchema.extend({
  mode: z.literal('SECTION'),
  section: z.enum(DiscoverEvents),
  items: z.array(EventCardSchema),
});

export const DiscoverResponseSchema = z.discriminatedUnion('mode', [
  DiscoverAggregateResponseSchema,
  DiscoverSectionResponseSchema,
]);

export const PagedEventsResponseSchema = PaginationResponseSchema.extend({
  items: z.array(EventCardSchema),
});

export const RegisterForEventRequestSchema = z.object({
  callbackUrl: z.url().optional(),
});

export const RegisterForEventResponseSchema = z.object({
  registration: z.object({
    id: z.uuid(),
    eventId: z.uuid(),
    status: z.enum(RegistrationStatus),
    expiresAt: z.iso.datetime().nullable(),
  }),
  paymentIntent: z
    .object({
      reference: z.string(),
      amount: MoneySchema,
      checkoutUrl: z.url().optional(),
    })
    .nullable(),
});

export const UpdateWatchProgressRequestSchema = z.object({
  positionSec: z
    .number()
    .int()
    .min(0)
    .max(24 * 60 * 60),
  durationSec: z.number().int().min(1).optional(),
});

/**
 * Replay resume point for a single event. Hydrates the player on load;
 * ongoing updates go over the `/viewing-session` socket's
 * `watch-progress` message (which reuses
 * `UpdateWatchProgressRequestSchema` as its payload).
 *
 * Never 404s — a viewer who has not watched yet reads back
 * `positionSec: 0` with null companions, so the FE has one shape to
 * handle.
 *
 * `percentage` is null when `durationSec` is unknown: the write path
 * accepts a position without a duration, and a percentage cannot be
 * derived from one alone.
 *
 * `completedAt` is sticky by design — it suppresses finished events
 * from the viewer's rails and feeds host replay analytics, so a
 * rewatch resumes from `positionSec` while this stays set. FE must
 * NOT gate resume on the card's `viewer.canResume`, which reads false
 * for a completed event.
 */
export const WatchProgressResponseSchema = z.object({
  eventId: z.uuid(),
  positionSec: z.number().int().min(0),
  durationSec: z.number().int().min(1).nullable(),
  percentage: z.number().min(0).max(100).nullable(),
  completedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});

/**
 * One broadcast segment of an event.
 *
 * An event has **many** of these, not one: the host pausing the stream
 * closes a segment and resuming opens the next, as does an encoder drop
 * and reconnect. `endedAt` is null only for a segment still on air.
 *
 * Deliberately free of ingest internals — no channel ARN, no provider
 * stream id, no `endReason`. Those describe our plumbing, not what the
 * viewer watched.
 */
export const EventStreamSessionSchema = z.object({
  id: z.uuid(),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
  /** Whole seconds on air; null while the segment is still running. */
  durationSec: z.number().int().min(0).nullable(),
});

/** `GET /viewer/events/:id/stream-sessions` — newest segment first. */
export const EventStreamSessionsResponseSchema = z.object({
  eventId: z.uuid(),
  sessions: z.array(EventStreamSessionSchema),
});

export const LiveCatchupSchema = z.object({
  playbackUrl: z.url(),
  expiresAt: z.iso.datetime(),
  streamStartedAt: z.iso.datetime(),
  dvrWindowSeconds: z.number().int().positive(),
});

export const LivePlaybackSchema = z.object({
  kind: z.literal('LIVE'),
  playbackUrl: z.url(),
  token: z.string(),
  expiresAt: z.iso.datetime(),
  catchup: LiveCatchupSchema.optional(),
});

export const ReplayPlaybackSchema = z.object({
  kind: z.literal('REPLAY'),
  playbackUrl: z.url(),
  expiresAt: z.iso.datetime(),
  /** Null until the replay chat export has run, or when it has no messages. */
  chatReplay: ChatReplayManifestSchema.nullable(),
});

// Union schema for runtime validation (e.g. ZodValidationPipe)
export const PlaybackResponseSchema = z.discriminatedUnion('kind', [
  LivePlaybackSchema,
  ReplayPlaybackSchema,
]);

export const MyEventCardSchema = EventCardSchema.extend({
  // Upcoming
  startsInSec: z.number().int().optional(),
  // Live
  liveViewerCount: z.number().int().min(0).optional(),
  liveStartedAt: z.iso.datetime().nullable().optional(),
  // Past
  replayAvailableUntil: z.iso.datetime().nullable().optional(),
  replayDurationSec: z.number().int().min(0).nullable().optional(),
  watchedPct: z.number().min(0).max(100).optional(),
  hasWatched: z.boolean().optional(),
  // Invites
  invitedAt: z.iso.datetime().optional(),
  /** The viewer hasn't opened this invite's event page yet. */
  isNewInvite: z.boolean().optional(),
});

export const MyEventsResponseSchema = PaginationResponseSchema.extend({
  tab: z.enum(MyEventsTabSchema),
  items: z.array(MyEventCardSchema),
  /** Invites the viewer hasn't opened yet; drives the Invites tab count. */
  newInvitesCount: z.number().int().nonnegative(),
});

export const MyEventsQuerySchema = PaginationParamsSchema.extend({
  tab: z.enum(MyEventsTabSchema),
  q: z
    .string()
    .trim()
    .max(80)
    .optional()
    .transform((value) => (value && value.length > 0 ? value : undefined)),
});

export const StartViewingSessionResponseSchema = z.object({
  sessionId: z.string(),
});

export const ViewingSessionHeartbeatResponseSchema = z.object({
  ok: z.literal(true),
});

export const EndViewingSessionResponseSchema = z.object({
  durationSec: z.number().int().min(0),
});

const retentionDaysSchema = z
  .number({
    error: (issue) =>
      issue.input === undefined || issue.input === null
        ? 'Please provide how many days the replay stays available.'
        : 'Replay retention must be a valid number of days.',
  })
  .int('Replay retention must be a whole number of days.')
  .min(1, 'Replay must be available for at least 1 day.')
  .max(365, 'Replay retention cannot exceed 365 days.');

const replayPriceSchema = z
  .number({
    error: (issue) =>
      issue.input === undefined || issue.input === null
        ? 'Please set a price for the paid replay.'
        : 'Replay price must be a valid number.',
  })
  .positive('Replay price must be greater than 0.');

export const ReplaySchema = z.discriminatedUnion(
  'access',
  [
    z.object({
      access: z.literal(EventReplayAccess.INCLUDED),
      retentionDays: retentionDaysSchema,
    }),
    z.object({
      access: z.literal(EventReplayAccess.PAID_SEPARATELY),
      retentionDays: retentionDaysSchema,
      price: replayPriceSchema,
    }),
    z.object({
      access: z.literal(EventReplayAccess.DISABLED),
    }),
  ],
  {
    error: (issue) =>
      issue.input === undefined ||
      issue.input === null ||
      (typeof issue.input === 'object' && (issue.input as { access?: unknown }).access == null)
        ? 'Please choose a replay access option.'
        : 'Invalid replay access option selected.',
  },
);

export const CurrencySchema = z.enum(CurrencyEnum);
export const CategorySchema = z.enum(EventCategory);
export const VisibilitySchema = z.enum(EventVisibility);

export const ViewingSessionModeSchema = z.enum(['LIVE_EDGE', 'CATCHUP']);

const WizardBasicsShape = {
  title: z.string().min(3).max(120),
  description: z.string().min(20).max(1200),
  category: CategorySchema,
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  durationMinutes: z.number().int().min(5).max(720),
  coverImageUrl: z.any().optional(),
  timezone: z.string(),
  previewClipUrl: z.any().optional(),
  tags: z.array(z.string().min(1).max(30)).max(10).optional(),
  location: z.string().max(120).optional(),
};

/**
 * Upper bound on a private event's guest list, typed or imported. Guests
 * only gate access when the event is `inviteOnly`; PUBLIC events ignore them.
 */
export const GUEST_LIST_MAX = 5000;

/** Imports from `POST /host/events/guest-imports` to link to the event. */
export const GuestImportIdsSchema = z.array(z.uuid()).max(20);

const WizardAccessFreeShape = {
  paid: z.literal(false),
  visibility: VisibilitySchema,
  /** PRIVATE only: true = only guests on the list can join (needs ≥1 guest). */
  inviteOnly: z.boolean().optional(),
  guestEmails: z.array(z.email()).max(GUEST_LIST_MAX).optional(),
  guestImportIds: GuestImportIdsSchema.optional(),
  /** Viewers can chat during the stream. Defaults to true. */
  liveChatEnabled: z.boolean().optional(),
  replay: ReplaySchema,
};

const WizardAccessPaidShape = {
  paid: z.literal(true),
  visibility: VisibilitySchema,
  /** PRIVATE only: true = only guests on the list can join (needs ≥1 guest). */
  inviteOnly: z.boolean().optional(),
  guestEmails: z.array(z.email()).max(GUEST_LIST_MAX).optional(),
  guestImportIds: GuestImportIdsSchema.optional(),
  /** Viewers can chat during the stream. Defaults to true. */
  liveChatEnabled: z.boolean().optional(),
  ticketPrice: z.number().positive(),
  ticketCurrency: CurrencySchema,
  ticketLimit: z.number().int().positive().nullable(),
  replay: ReplaySchema,
};

const Step1Schema = z.object({ step: z.literal(1), ...WizardBasicsShape });

const Step2FreeSchema = z.object({ step: z.literal(2), ...WizardAccessFreeShape });

const Step2PaidSchema = z.object({ step: z.literal(2), ...WizardAccessPaidShape });

/** `POST /host/events/wizard` body: basics + access & pricing in one request. */
export const HostEventWizardCreateSchema = z.discriminatedUnion('paid', [
  z.object({ ...WizardBasicsShape, ...WizardAccessFreeShape }),
  z.object({ ...WizardBasicsShape, ...WizardAccessPaidShape }),
]);

const Step3SelfSchema = z.object({
  step: z.literal(3),
  mode: z.literal(EventProductionMode.SELF),
  streamingMode: z.enum(EventStreamingMode),
});

const Step3HireSchema = z.object({
  step: z.literal(3),
  mode: z.literal(EventProductionMode.HIRE),
  /**
   * Legacy Direct Hire: non-empty `crewIds` still creates REQUESTED bookings
   * via `createForEvent`. Assign Role FE may send `[]` and use
   * `/crew-assignments` instead (booking only on vendor accept).
   */
  crewIds: z.array(z.uuid()).min(1).max(5).default([]),
  notes: z.string().max(500).optional(),
});

export const StartViewingSessionRequestSchema = z.object({
  deviceType: z.string().max(50).optional(),
  mode: ViewingSessionModeSchema.default('LIVE_EDGE').optional(),
});

export const PatchStreamControlRequestSchema = z
  .object({
    streamEnabled: z.boolean(),
  })
  .strict();

export const HostEventWizardPatchSchema = z.union([
  Step1Schema,
  Step2FreeSchema,
  Step2PaidSchema,
  Step3SelfSchema,
  Step3HireSchema,
]);

const Currency = z.string().min(3).max(8);

const IsoDateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');

// ─── Query DTOs ───────────────────────────────────────────────────

export const HostEarningsSummaryQuerySchema = z
  .object({
    from: IsoDateOnly,
    to: IsoDateOnly,
    eventId: z.uuid().optional(),
    currency: Currency.optional(),
  })
  .strict();

export const HostEarningsBreakdownQuerySchema = z
  .object({
    from: IsoDateOnly,
    to: IsoDateOnly,
    currency: Currency.optional(),
    pageNumber: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().trim().min(1).max(100).optional(),
  })
  .strict();

export const HostEarningsTimeseriesQuerySchema = z
  .object({
    from: IsoDateOnly,
    to: IsoDateOnly,
    granularity: z.enum(['daily', 'weekly']),
    currency: Currency.optional(),
  })
  .strict();

// ─── Response DTOs ─────────────────────────────────────────────────

/**
 * Earnings summary card. Each money field is a MoneyDto per
 * MONEY_MULTI_CURRENCY.md §4 (amount + currency colocated). The
 * previously-top-level `currency` is gone — every amount carries its
 * unit so the FE never has to remember which currency applies where.
 */
export const HostEarningsSummaryResponseSchema = z
  .object({
    from: IsoDateOnly,
    to: IsoDateOnly,
    /** Host's take of ticket revenue in the range. */
    gross: MoneySchema,
    /** Refunds reversed in the range. */
    refunds: MoneySchema,
    /** gross − refunds. */
    net: MoneySchema,
    /** Live withdrawable balance from the ledger (not the rollup). */
    available: MoneySchema,
    /** Live HOST_PENDING balance from the ledger (not the rollup). */
    pending: MoneySchema,
    ticketsSold: z.number().int().nonnegative(),
    avgTicketPrice: MoneySchema,
    previousPeriodDelta: z.object({
      percentage: z.number().nullable(),
      /**
       * Direction of the change vs the prior range. Always defined
       * (unlike `percentage`, which is null when prior gross was zero).
       */
      direction: DeltaDirectionSchema,
      /** Prior range gross (MoneyDto). */
      gross: MoneySchema,
    }),
    revenueSplit: z.object({
      hostShare: MoneySchema,
      platformFee: MoneySchema,
      crewSalaries: MoneySchema,
    }),
  })
  .strict();

export const HostEarningsBreakdownItemSchema = z
  .object({
    eventId: z.uuid(),
    eventTitle: z.string(),
    gross: MoneySchema,
    refunds: MoneySchema,
    net: MoneySchema,
    /** Earliest day the event had ledger activity in this range. */
    firstActivityAt: IsoDateOnly,
    /** Latest day the event had ledger activity in this range. */
    lastActivityAt: IsoDateOnly,
    ticketsSold: z.number().int().nonnegative(),
    status: z.enum(['COMPLETED', 'LIVE', 'SCHEDULED']),
  })
  .strict();

export const HostEarningsBreakdownResponseSchema = z
  .object({
    items: z.array(HostEarningsBreakdownItemSchema),
    ...PaginationResponseSchema.shape,
  })
  .strict();

export const HostEarningsTimeseriesPointSchema = z
  .object({
    /** ISO date. For weekly, the Monday-start of the bucket. */
    date: IsoDateOnly,
    gross: MoneySchema,
    refunds: MoneySchema,
    net: MoneySchema,
  })
  .strict();

/**
 * Top-level `currency` dropped — each point's MoneyDto carries its
 * unit (spec §4). Callers rendering a chart title still get the
 * currency via `points[i].gross.currency`.
 */
export const HostEarningsTimeseriesResponseSchema = z
  .object({
    granularity: z.enum(['daily', 'weekly']),
    points: z.array(HostEarningsTimeseriesPointSchema),
  })
  .strict();

// --- Public (unauthenticated) event pages ---
export const PublicEventStatusSchema = z.enum(['ALL', 'LIVE', 'UPCOMING', 'PAST']);

export const PublicEventCardSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  timezone: z.string(),
  description: z.string().nullable(),
  coverImageUrl: z.url().nullable(),
  previewClipUrl: z.string().nullable(),
  category: z.enum(EventCategory),
  accessModel: z.enum(EventAccessModel),
  ticketPrice: MoneySchema.nullable(),
  isFeatured: z.boolean(),
  scheduledStartLocal: z.string(),
  scheduledStart: z.iso.datetime(),
  hostDisplayName: z.string().nullable(),
});

export const PublicEventsQuerySchema = PaginationParamsSchema.extend({
  status: PublicEventStatusSchema.default('ALL'),
  category: z.enum(EventCategory).optional(),
  q: z
    .string()
    .trim()
    .max(100)
    .optional()
    .transform((value) => (value && value.length > 0 ? value : undefined)),
});

export const PublicEventsResponseSchema = PaginationResponseSchema.extend({
  items: z.array(PublicEventCardSchema),
});

// Ingest-substate discriminants for the anonymous player. Defined
// inline (instead of importing `AwaitingIngestSchema` from
// `../payment`) to avoid a payment↔event schema cycle — payment.schemas
// already imports enums from `../event`.
const PublicTrailerSchema = z.object({
  kind: z.literal('TRAILER'),
  trailerUrl: z.string(),
});
const PublicSlateSchema = z.object({
  kind: z.literal('SLATE'),
  slate: z.object({
    title: z.string(),
    hostDisplayName: z.string().nullable(),
    scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  }),
});
const PublicAwaitingIngestSchema = z.discriminatedUnion('kind', [
  PublicTrailerSchema,
  PublicSlateSchema,
]);

// Single-event detail for the anonymous/share landing page. Mirrors
// ViewerEventDetailResponseSchema but WITHOUT the `viewer` block —
// entitlement, purchaseCta, activeIntentReference all require a
// resolved user. Any published event resolves here by id, so a PRIVATE
// event's link works before sign-in; only PUBLIC events are ever listed.
export const PublicEventDetailResponseSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  coverImageUrl: z.url().nullable(),
  category: z.enum(EventCategory),
  timezone: z.string(),
  scheduledStart: z.iso.datetime(),
  scheduledEnd: z.iso.datetime(),
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  scheduledEndLocal: z.iso.datetime({ local: true, offset: false }),
  state: z.enum(EventState),
  accessModel: z.enum(EventAccessModel),
  visibility: z.enum(EventVisibility),
  ticketPrice: MoneySchema.nullable(),
  replayAccess: z.enum(EventReplayAccess),
  replayAccessFee: MoneySchema.nullable(),
  isFeatured: z.boolean(),
  hostDisplayName: z.string().nullable(),
  hostBio: z.string().nullable(),
  previewClipUrl: z.string().nullable(),
  remainingTickets: z.number().int().nullable(),
  liveStartedAt: z.iso.datetime().nullable(),
  liveEndedAt: z.iso.datetime().nullable(),
  replayAvailableUntil: z.iso.datetime().nullable(),
  ingestSubstate: z.enum(['AWAITING_INGEST', 'STREAMING']).nullable(),
  awaitingIngestContent: PublicAwaitingIngestSchema.nullable(),
  /** Only guests on the host's list can join; sign-in is required to check. */
  inviteOnly: z.boolean(),
});

/**
 * `GET /public/invites/:token` — what the invite email already told the
 * recipient, so the sign-up page can show the event before an account exists.
 */
export const EventInvitePreviewResponseSchema = z.object({
  eventId: z.uuid(),
  title: z.string(),
  coverImageUrl: z.string().nullable(),
  scheduledStart: z.iso.datetime(),
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  timezone: z.string(),
  hostDisplayName: z.string().nullable(),
  /** The invited address; sign-up is locked to it. */
  email: z.email(),
  /** An account already uses this email — send the viewer to sign in. */
  hasAccount: z.boolean(),
});

export const HostLivePreviewSchema = z.object({
  playbackUrl: z.url(),
  token: z.string(),
  expiresAt: z.iso.datetime(),
});

export const PatchReplayPeriodRequestSchema = z.object({
  retentionDays: z.number().int().min(1).max(90),
});

export const PatchReplayPeriodResponseSchema = z.object({
  eventId: z.uuid(),
  liveEndedAt: z.iso.datetime(),
  retentionDays: z.number().int().min(1).max(90),
  replayAvailableUntil: z.iso.datetime(),
});

export const HostEventTicketSalesTimelineQuerySchema = z.object({
  granularity: z.enum(['daily']).optional().default('daily'),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'from must be YYYY-MM-DD')
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'to must be YYYY-MM-DD')
    .optional(),
});

export const HostEventTicketSalesTimelinePointSchema = z.object({
  /** UTC-anchored `YYYY-MM-DD` — one point per calendar day in the range. */
  date: z.string(),
  /** SUCCEEDED PaymentIntent count for this day. */
  ticketsSold: z.number().int().nonnegative(),
  /** Per-day SUCCEEDED payment total as a MoneyDto (§4). */
  revenue: MoneySchema,
});

/**
 * Top-level `currency` dropped — every point's revenue MoneyDto carries
 * its own unit per MONEY_MULTI_CURRENCY.md §4.
 */
export const HostEventTicketSalesTimelineResponseSchema = z.object({
  eventId: z.uuid(),
  granularity: z.enum(['daily']),
  from: z.string(),
  to: z.string(),
  points: z.array(HostEventTicketSalesTimelinePointSchema),
});

export const HostAudienceQuerySchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'from must be YYYY-MM-DD')
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'to must be YYYY-MM-DD')
    .optional(),
  eventId: z.uuid().optional(),
});

/** Cohort as `{ count, pct }` — pct is a 0..1 ratio, rounded to 4dp. */
const CohortSegmentSchema = z.object({
  count: z.number().int().nonnegative(),
  pct: z.number().min(0).max(1),
});

/**
 * Prior-period comparison tile shape. `percentagePrev` is `null` when
 * the previous period had zero so the FE renders "N/A" instead of
 * dividing by zero. Delta rounded to one decimal. `direction` is
 * always defined (derived from raw current vs previous).
 */
const CountWithPrevDeltaSchema = z.object({
  count: z.number().int().nonnegative(),
  percentagePrev: z.number().nullable(),
  direction: DeltaDirectionSchema,
});

export const HostAudienceSummaryResponseSchema = z.object({
  /** Range echoed back so the FE doesn't have to guess when omitted. */
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  currency: z.string(),
  summary: z.object({
    /**
     * Total viewing-session count across all in-range events plus
     * a percentage delta vs the mirrored prior period.
     */
    totalViewers: CountWithPrevDeltaSchema,
    /** Distinct viewer count across all in-range events. */
    uniqueViewers: z.number().int().nonnegative(),
    /** Distinct viewers who both watched AND paid in the range. */
    liveSpenders: z.number().int().nonnegative(),
    /** Distinct viewers who watched but never paid (any host event). */
    casualViewers: z.number().int().nonnegative(),
    /** Distinct viewers with `>= 2` sessions inside the requested range. */
    repeatViewers: z.number().int().nonnegative(),
    /** Distinct viewers whose first-ever host session lies in-range. */
    newThisPeriod: z.number().int().nonnegative(),
  }),
  segments: z.object({
    /** Viewers whose first-ever session with this host lies in-range. */
    newViewers: CohortSegmentSchema,
    /** Viewers with `>= RETURNING_VIEWER_MIN_EVENTS` lifetime host events. */
    returningViewers: CohortSegmentSchema,
    /** Viewers with lifetime spend `>= HIGH_VALUE_SPEND_MINOR`. */
    highValueBuyers: CohortSegmentSchema,
    /** Viewers with zero lifetime spend on any of this host's events. */
    casualViewers: CohortSegmentSchema,
  }),
  revenuePerViewer: z.object({
    /**
     * Distinct viewers who paid at least once inside the requested
     * range. Period-scoped, so does NOT necessarily equal
     * `bands.low + bands.mid + bands.high` (bands are lifetime-based).
     */
    total: z.number().int().nonnegative(),
    /**
     * Mean in-range spend per in-range spender as a MoneyDto (§4).
     * Amount is 0 when there are no in-range spenders; currency is
     * still the host's default so the FE can render "₦0.00".
     */
    averageSpend: MoneySchema,
    /**
     * Percentage delta of `averageSpend` vs the mirrored prior period.
     * `null` when the prior average was zero.
     */
    percentagePrev: z.number().nullable(),
    /**
     * Direction of the change vs the mirrored prior period. Always
     * defined (unlike `percentagePrev`).
     */
    direction: DeltaDirectionSchema,
    /**
     * Cohort split by *lifetime* spend across this host's events.
     * Kept as the pre-existing shape so the FE bars keep working.
     */
    bands: z.object({
      lowSpenders: z.number().int().nonnegative(),
      midSpenders: z.number().int().nonnegative(),
      highSpenders: z.number().int().nonnegative(),
    }),
  }),
});

export const HostAudienceEngagementQuerySchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'from must be YYYY-MM-DD')
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'to must be YYYY-MM-DD')
    .optional(),
  /** Case-insensitive event title search. */
  search: z.string().trim().min(1).max(120).optional(),
  ...PaginationParamsSchema.shape,
});

const HostAudienceEngagementItemSchema = z.object({
  eventId: z.uuid(),
  eventTitle: z.string(),
  /** Distinct viewers for this event inside the requested range. */
  viewers: z.number().int().nonnegative(),
  /**
   * Peak concurrent live viewers ever observed for this event
   * (lifetime — not scoped to the range). Reads from
   * `event_audience_summaries.peak_concurrent`.
   */
  peakConcurrent: z.number().int().nonnegative(),
  /** Replay view count for this event inside the requested range. */
  replays: z.number().int().nonnegative(),
});

export const HostAudienceEngagementResponseSchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  items: z.array(HostAudienceEngagementItemSchema),
  ...PaginationResponseSchema.shape,
});

export const HostAudienceTopViewersQuerySchema = z.object({
  /** 1..50 top spenders. Defaults to 10 to match the mock. */
  limit: z.coerce.number().int().min(1).max(50).optional().default(10),
});

const TopViewerItemSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  avatarKey: z.string().nullable(),
  /** Lifetime spend across this host's events (MoneyDto). */
  totalSpend: MoneySchema,
  /** Number of distinct paid events from this host. */
  eventCount: z.number().int().nonnegative(),
});

/**
 * `currency` is intentionally omitted at the response level — each
 * item carries its own `MoneyDto` per MONEY_MULTI_CURRENCY.md §4.
 */
export const HostAudienceTopViewersResponseSchema = z.object({
  items: z.array(TopViewerItemSchema),
});

const ActivityFeedItemSchema = z.object({
  kind: z.enum(['TICKET_SOLD', 'REPLAY_SOLD']),
  subjectType: z.enum(['Event', 'PaymentIntent', 'ReplayPurchase', 'Booking']),
  subjectId: z.string(),
  /** Unix ms — matches the ZSET score. */
  occurredAt: z.number(),
  /** Free-form payload from the host activity emitter. */
  payload: z.record(z.string(), z.unknown()),
});

export const HostAudienceActivityFeedResponseSchema = z.object({
  items: z.array(ActivityFeedItemSchema),
  ...PaginationResponseSchema.shape,
});

export const HostAudienceGeoResponseSchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  items: z.array(
    z.object({
      /** ISO 3166-1 alpha-2 uppercase, or `UNKNOWN` when geo-ip failed. */
      country: z.string(),
      viewerCount: z.number().int().nonnegative(),
    }),
  ),
});

export const HostAudienceDevicesResponseSchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  items: z.array(
    z.object({
      /** Normalised lowercase bucket (`mobile`, `desktop`, `tablet`, `unknown`). */
      deviceType: z.string(),
      viewerCount: z.number().int().nonnegative(),
    }),
  ),
});

export const HostAudienceViewerActivityResponseSchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  granularity: z.enum(['daily']),
  points: z.array(
    z.object({
      /** UTC-anchored `YYYY-MM-DD`; one point per calendar day in range. */
      date: z.string(),
      viewers: z.number().int().nonnegative(),
    }),
  ),
});

export const HostAudienceViewerActivityQuerySchema = HostAudienceQuerySchema.extend({
  granularity: z.enum(['daily']).optional().default('daily'),
});

// ─── /viewing-session socket wire contracts ───────────────────────────

/**
 * Discriminates the LIVE substate for the viewer player. Matches the
 * `ingestSubstate` field on `PublicEventDetailResponse`.
 *   - `AWAITING_INGEST`: host went LIVE, no ingest frames yet.
 *   - `STREAMING`: ingest received at least once.
 *   - `null`: any non-LIVE state.
 */
export const IngestSubstateSchema = z.enum(['AWAITING_INGEST', 'STREAMING']).nullable();

/**
 * Lifecycle state feed (Event.state) pushed over the `/viewing-session`
 * namespace. Snapshot + transitions share this shape; `isSnapshot`
 * discriminates a connect-time read from a real transition.
 */
export const EventStatePayloadSchema = z.object({
  eventId: z.string(),
  state: z.enum(EventState),
  previousState: z.enum(EventState).nullable(),
  occurredAt: z.iso.datetime(),
  isSnapshot: z.literal(true).optional(),
});

/**
 * Ingest-substate feed pushed over the `/viewing-session` namespace.
 * Distinct from `EventStatePayload` because the LIVE lifecycle doesn't
 * move when ingest starts.
 */
export const StreamStatePayloadSchema = z.object({
  eventId: z.string(),
  ingestSubstate: IngestSubstateSchema,
  /** First-frame timestamp; null unless `ingestSubstate` is STREAMING. */
  startedAt: z.iso.datetime().nullable(),
  isSnapshot: z.literal(true).optional(),
});

export const StreamControlPayloadSchema = z.object({
  eventId: z.string(),
  streamEnabled: z.boolean(),
  occurredAt: z.iso.datetime(),
  isSnapshot: z.literal(true).optional(),
});

/**
 * ACK for the `heartbeat` frame on the `/viewing-session` namespace.
 * `reason` is set only when `ok === false`; the socket is closed by
 * the server on the same tick.
 */
export const ViewingSessionHeartbeatAckSchema = z.object({
  ok: z.boolean(),
  reason: z.enum(['unauthorized', 'session_ended', 'not_owner', 'internal']).optional(),
});

/**
 * ACK for `watch-progress` writes. Failure leaves the socket open —
 * see the gateway rationale.
 */
export const ViewingSessionWatchProgressAckSchema = z.object({
  ok: z.boolean(),
  /** Position now in effect — may trail the sent value when coalesced. */
  positionSec: z.number().nonnegative().optional(),
  reason: z.enum(['unauthorized', 'invalid_payload', 'not_replayable', 'internal']).optional(),
});

/**
 * ACK for `watch-progress-get` reads. Mirrors the shape of
 * `GET /viewer/events/:id/watch-progress`.
 */
export const ViewingSessionWatchProgressGetAckSchema = z.object({
  ok: z.boolean(),
  progress: WatchProgressResponseSchema.optional(),
  reason: z.enum(['unauthorized', 'internal']).optional(),
});

// ─── /crew-presence socket wire contracts ─────────────────────────────

/**
 * ACK for `heartbeat` on the `/crew-presence` namespace. `firstBeatOfSession`
 * is set on the first beat of a fresh presence session so the host
 * console can render a "joined" chip.
 */
export const CrewPresenceHeartbeatAckSchema = z.object({
  ok: z.boolean(),
  reason: z.enum(['unauthorized', 'not_crew', 'internal']).optional(),
  firstBeatOfSession: z.boolean().optional(),
});

// ─── namespace event-name unions ──────────────────────────────────────

/** Client → server frame names on `/viewing-session`. */
export const ViewingSessionEvents = {
  Heartbeat: 'heartbeat',
  WatchProgress: 'watch-progress',
  WatchProgressGet: 'watch-progress-get',
} as const;

/** Server → client frame names on `/viewing-session`. */
export const ViewingSessionServerEvents = {
  EventState: 'event-state',
  StreamState: 'stream-state',
  StreamControl: 'stream-control',
} as const;

/** Server → client frame names on `/viewer-events`. */
export const ViewerEventsBusEvents = {
  StateChanged: 'event.state.changed',
  Updated: 'event.updated',
  Cancelled: 'event.cancelled',
  StreamStarted: 'event.stream.started',
  StreamControl: 'event.stream.control_changed',
  Extended: 'event.extended',
} as const;

/** Client → server frame names on `/crew-presence`. */
export const CrewPresenceEvents = {
  Heartbeat: 'heartbeat',
} as const;
